import React from "react";
import { mediaUrl } from "../lib/api";
import "./styles/BlocksRenderer.css";

/**
 * Renders Strapi v5 Blocks (Rich Text) JSON to React elements.
 * Handles: paragraph, heading, list (incl. nested), quote, code,
 * image, divider, link, and inline bold/italic/underline/strike/code.
 * Soft line breaks (Shift+Enter in the Strapi editor) become <br>.
 *
 * All styling lives in ./styles/BlocksRenderer.css under .rich-text
 * (no inline styles), so pages can override it with plain CSS.
 *
 * Usage: <BlocksRenderer content={attrs.content} />
 */
export default function BlocksRenderer({ content, className = "" }) {
  if (!Array.isArray(content) || content.length === 0) return null;
  return (
    <div className={`rich-text ${className}`.trim()}>
      {content.map((block, i) => renderBlock(block, i))}
    </div>
  );
}

function renderBlock(block, key) {
  switch (block?.type) {
    case "paragraph": {
      // Empty paragraph = editor pressed Enter for spacing.
      // Rendered with its own class so pages can style it (e.g. as a section gap).
      if (isEmpty(block.children)) {
        return <p key={key} className="rich-text__blank" aria-hidden="true">{"\u00A0"}</p>;
      }
      return <p key={key}>{renderChildren(block.children)}</p>;
    }

    case "heading": {
      const level = Math.min(Math.max(Number(block.level) || 2, 1), 6);
      const Tag = `h${level}`;
      return <Tag key={key}>{renderChildren(block.children)}</Tag>;
    }

    case "list":
      return renderList(block, key);

    case "quote":
      return <blockquote key={key}>{renderChildren(block.children)}</blockquote>;

    case "code":
      return (
        <pre key={key}>
          <code>{block.plainText ?? textOf(block.children)}</code>
        </pre>
      );

    case "image": {
      const src = mediaUrl(block.image);
      if (!src) return null;
      return (
        <figure key={key} className="rich-text__figure">
          <img src={src} alt={block.image?.alternativeText || ""} loading="lazy" />
          {block.image?.caption ? <figcaption>{block.image.caption}</figcaption> : null}
        </figure>
      );
    }

    case "divider":
      return <hr key={key} />;

    default:
      return null;
  }
}

/* Lists: Strapi nests an indented list either as a child of a list-item
   or as a sibling node inside the parent list — handle both. */
function renderList(block, key) {
  const Tag = block.format === "ordered" ? "ol" : "ul";
  return (
    <Tag key={key}>
      {(block.children || []).map((item, i) =>
        item.type === "list" ? (
          renderList(item, i)
        ) : (
          <li key={i}>{renderChildren(item.children)}</li>
        )
      )}
    </Tag>
  );
}

function renderChildren(children) {
  if (!Array.isArray(children)) return null;
  return children.map((child, i) => renderInline(child, i));
}

function renderInline(node, key) {
  if (!node) return null;

  if (node.type === "list") {
    return renderList(node, key);
  }

  if (node.type === "link") {
    const isExternal = /^https?:\/\//.test(node.url || "");
    return (
      <a
        key={key}
        href={node.url}
        {...(isExternal ? { target: "_blank", rel: "noopener noreferrer" } : {})}
      >
        {renderChildren(node.children)}
      </a>
    );
  }

  if (node.type === "text") {
    if (!node.text) return null;
    let el = withLineBreaks(node.text, key);
    if (node.code) el = <code>{el}</code>;
    if (node.bold) el = <strong>{el}</strong>;
    if (node.italic) el = <em>{el}</em>;
    if (node.underline) el = <u>{el}</u>;
    if (node.strikethrough) el = <s>{el}</s>;
    return <React.Fragment key={key}>{el}</React.Fragment>;
  }

  return null;
}

/* "\n" inside a text node (Shift+Enter) → real <br> */
function withLineBreaks(text, key) {
  const parts = text.split("\n");
  if (parts.length === 1) return text;
  return parts.flatMap((part, i) =>
    i === 0 ? [part] : [<br key={`${key}-br-${i}`} />, part]
  );
}

function isEmpty(children) {
  return !Array.isArray(children) || children.every((c) => c.type === "text" && !c.text);
}

function textOf(children) {
  if (!Array.isArray(children)) return "";
  return children.map((c) => (c.text != null ? c.text : textOf(c.children))).join("");
}